import * as React from 'react';
import { cn } from '@/lib/utils';
import { MemberDetailDrawer } from '@/pages/members/MemberDetailDrawer';

/**
 * App-wide member viewer. `MemberDetailDrawer` is self-sufficient (it fetches
 * everything by id), so a single instance mounted once at the authenticated
 * shell lets any table/list open a member's detail drawer by id — no per-page
 * drawer state or data threading. Consume via {@link useMemberDrawer} /
 * {@link MemberNameCell}.
 */
interface MemberDrawerCtx {
  openMember: (memberId: string) => void;
}

// Default is a no-op so a `MemberNameCell` rendered outside the provider (e.g.
// in an isolated component test) still renders its label; the click is inert.
const MemberDrawerContext = React.createContext<MemberDrawerCtx>({ openMember: () => {} });

export function useMemberDrawer(): MemberDrawerCtx {
  return React.useContext(MemberDrawerContext);
}

export function MemberDrawerProvider({ children }: { children: React.ReactNode }) {
  const [memberId, setMemberId] = React.useState<string | null>(null);
  const value = React.useMemo<MemberDrawerCtx>(() => ({ openMember: (id) => setMemberId(id) }), []);
  return (
    <MemberDrawerContext.Provider value={value}>
      {children}
      <MemberDetailDrawer memberId={memberId} onClose={() => setMemberId(null)} />
    </MemberDrawerContext.Provider>
  );
}

/**
 * Member-name table/list cell: renders the resolved username as a link that
 * opens the member viewer, falling back to a truncated monospace id while the
 * username lookup is pending (mirrors `nameCell`, but clickable). Use for member
 * columns only — partner/admin/agent/provider names have no member drawer.
 */
export function MemberNameCell({ name, id }: { name?: string | null; id?: string | null }) {
  const { openMember } = useMemberDrawer();
  if (!id) return <span className="text-muted-foreground">-</span>;
  const label = name ?? id.slice(0, 8);
  return (
    <button
      type="button"
      title={id}
      onClick={(e) => {
        e.stopPropagation();
        openMember(id);
      }}
      className={cn(
        'cursor-pointer text-left text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none',
        !name && 'font-mono text-xs',
      )}
    >
      {label}
    </button>
  );
}
